/**
 * Pure helpers for the Mediathek admin (src/pages-dev-admin/mediathek):
 * constants, sort rules, form <-> mutation input conversion, validation and
 * upload helpers.
 *
 * Kept free of React, i18next and Amplify so it can be tested with node:test,
 * see scripts/slug-frontend/mediathek.test.js. Error messages are English
 * literal i18n keys; the UI passes them through t().
 *
 * The sort rules and the video embed rules mirror the public API lambda
 * (amplify/backend/function/getPublicMedia/src/media.js) - keep them in sync.
 */

export const MEDIA_SERIES_TYPES = ['podcast', 'video'];
export const MEDIA_STATUSES = ['draft', 'published'];
export const DEFAULT_MEDIA_STATUS = 'draft';

export const PUBLIC_BUCKET_URL = 'https://iec-cp-public.s3.eu-central-1.amazonaws.com';
/** uploadCompanyImage imageType for series covers and item images (prefix mediathekImages/) */
export const MEDIATHEK_IMAGE_TYPE = 'mediathekImage';
export const MEDIATHEK_IMAGE_PREFIX = 'mediathekImages/';
/** Amplify Storage (accessLevel guest => "public/" + key) staging prefix for audio/video uploads */
export const MEDIATHEK_UPLOAD_PREFIX = 'mediathekUploads/';

const MB = 1024 * 1024;
/**
 * Images are sent base64-encoded inside the uploadCompanyImage GraphQL request (+33 %).
 * That request passes an AppSync Lambda resolver: the evaluated request template is capped
 * at 5 MB and a synchronous Lambda invoke at 6 MB, so 3 MiB (= 4 MiB base64) keeps a safe
 * margin for the event envelope. The contract's 10 MB cannot be reached over this transport.
 */
export const IMAGE_UPLOAD_RULES = { extensions: ['jpg', 'jpeg', 'png'], maxBytes: 3 * MB };
export const MEDIA_UPLOAD_RULES = {
  audio: { extensions: ['mp3', 'm4a', 'wav', 'ogg', 'aac'], maxBytes: 300 * MB },
  video: { extensions: ['mp4', 'webm', 'mov', 'm4v'], maxBytes: 2000 * MB },
};

const INT_MIN = -2147483648;
const INT_MAX = 2147483647;

// ---------------------------------------------------------------------------
// Generic value helpers
// ---------------------------------------------------------------------------

/** Text of a MultiLangString list in `language`, falling back to de, en, then the first non-empty entry. */
export function getLocalized(value, language = 'de') {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return '';
  const entries = value.filter((e) => e && typeof e.text === 'string' && e.text.trim() !== '');
  for (const lang of [language, 'de', 'en']) {
    const hit = entries.find((e) => e.language === lang);
    if (hit) return hit.text;
  }
  return entries.length ? entries[0].text : '';
}

/** Exact text of one language ('' when missing) - for form inputs. */
export function getLanguageText(value, language) {
  if (!Array.isArray(value)) return '';
  const hit = value.find((e) => e && e.language === language);
  return hit && typeof hit.text === 'string' ? hit.text : '';
}

/** New MultiLangString list with the text of `language` replaced (other languages kept, order de, en, rest). */
export function setLocalized(value, language, text) {
  const list = Array.isArray(value) ? value.filter((e) => e && e.language && e.language !== language) : [];
  list.push({ language, text });
  const rank = (lang) => (lang === 'de' ? 0 : lang === 'en' ? 1 : 2);
  return list.sort((a, b) => rank(a.language) - rank(b.language));
}

/** Removes __typename keys recursively. */
export function stripTypename(value) {
  if (Array.isArray(value)) return value.map(stripTypename);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, v] of Object.entries(value)) {
      if (key !== '__typename') out[key] = stripTypename(v);
    }
    return out;
  }
  return value;
}

/** true for '', null, undefined and whole numbers (number or numeric string) within the GraphQL Int range. */
export function isIntegerLike(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number') return Number.isInteger(value) && value >= INT_MIN && value <= INT_MAX;
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (s === '') return true;
  if (!/^-?\d+$/.test(s)) return false;
  const n = Number(s);
  return n >= INT_MIN && n <= INT_MAX;
}

/** Whole number or null ('' / invalid -> null). */
export function toIntOrNull(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  if (!isIntegerLike(value)) return null;
  return typeof value === 'number' ? value : Number(value.trim());
}

/** Trimmed string or null for empty/non-string values. */
export function emptyToNull(value) {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  return s === '' ? null : s;
}

/**
 * null for "empty" editor HTML (<p></p>, <p><br></p>, whitespace, &nbsp;), the
 * unchanged HTML otherwise. Media-only content (img, iframe, video, audio) counts
 * as content.
 */
export function emptyHtmlToNull(html) {
  if (typeof html !== 'string') return null;
  if (/<(img|iframe|video|audio|embed|object|hr)\b/i.test(html)) return html;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .trim();
  return text === '' ? null : html;
}

/** http(s) URL check (schemeless input is not accepted). */
export function isValidHttpUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    const url = new URL(value.trim());
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
  } catch (e) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Dates (<input type="datetime-local"> works in local time without offset)
// ---------------------------------------------------------------------------

const pad = (n) => String(n).padStart(2, '0');

/** ISO string -> "YYYY-MM-DDTHH:mm" in local time ('' when empty/invalid). */
export function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "YYYY-MM-DDTHH:mm" (local time) -> ISO string (UTC), null when empty/invalid. */
export function fromDatetimeLocal(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] || 0));
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Current time rounded down to the minute, as ISO string (default publishedAt of new items). */
export function nowIsoMinute(now = new Date()) {
  const d = new Date(now.getTime());
  d.setSeconds(0, 0);
  return d.toISOString();
}

function timeOrNull(value) {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function compareIdAsc(a, b) {
  const x = String(a?.id ?? '');
  const y = String(b?.id ?? '');
  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
}

/** Ascending, null FIRST. */
function compareAscNullFirst(x, y) {
  if (x === y) return 0;
  if (x === null) return -1;
  if (y === null) return 1;
  return x - y;
}

/** Ascending, null LAST. */
function compareAscNullLast(x, y) {
  if (x === y) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return x - y;
}

/** Descending, null LAST. */
function compareDescNullLast(x, y) {
  if (x === y) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return y - x;
}

// ---------------------------------------------------------------------------
// Sort rules (shared with the public API)
// ---------------------------------------------------------------------------

/**
 * Display order of the items of a series: sortKey asc (null FIRST, so new items
 * appear on top), publishedAt desc (null last), episodeNumber desc (null last),
 * createdAt desc (null last), id asc.
 */
export function compareMediaItems(a, b) {
  return compareAscNullFirst(numberOrNull(a?.sortKey), numberOrNull(b?.sortKey))
    || compareDescNullLast(timeOrNull(a?.publishedAt), timeOrNull(b?.publishedAt))
    || compareDescNullLast(numberOrNull(a?.episodeNumber), numberOrNull(b?.episodeNumber))
    || compareDescNullLast(timeOrNull(a?.createdAt), timeOrNull(b?.createdAt))
    || compareIdAsc(a, b);
}

/** Flat item list order: publishedAt desc (null last), createdAt desc, id asc. */
export function compareMediaItemsByDate(a, b) {
  return compareDescNullLast(timeOrNull(a?.publishedAt), timeOrNull(b?.publishedAt))
    || compareDescNullLast(timeOrNull(a?.createdAt), timeOrNull(b?.createdAt))
    || compareIdAsc(a, b);
}

/** Series order: sortKey asc (null last), German title asc, id asc. */
export function compareMediaSeries(a, b) {
  const bySortKey = compareAscNullLast(numberOrNull(a?.sortKey), numberOrNull(b?.sortKey));
  if (bySortKey) return bySortKey;
  const ta = getLocalized(a?.title, 'de');
  const tb = getLocalized(b?.title, 'de');
  const byTitle = ta.localeCompare(tb, 'de', { sensitivity: 'base' });
  return byTitle || compareIdAsc(a, b);
}

/** New array with the element at `from` moved to `to`. */
export function moveItem(list, from, to) {
  const out = Array.isArray(list) ? [...list] : [];
  if (from < 0 || from >= out.length || to < 0 || to >= out.length) return out;
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved);
  return out;
}

/** sortKey 10, 20, 30, ... for every item in the given order. */
export function reorderSortKeys(items, step = 10) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({ id: item.id, sortKey: (index + 1) * step }));
}

// ---------------------------------------------------------------------------
// Video URLs (same rules as the public API lambda)
// ---------------------------------------------------------------------------

function parseHttpUrl(value) {
  if (typeof value !== 'string') return null;
  let s = value.trim();
  if (!s) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = `https://${s.replace(/^\/+/, '')}`;
  try {
    const url = new URL(s);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url;
  } catch (e) {
    return null;
  }
}

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'youtube-nocookie.com', 'music.youtube.com']);

/** Host without a leading "www." / "m.". */
function bareHost(url) {
  return url.hostname.toLowerCase().replace(/^(www|m)\./, '');
}

/** YouTube video id (11 characters) of a watch / youtu.be / embed / shorts / live URL, else null. "videoseries" (playlist embeds) is not a video id. */
export function youtubeId(value) {
  const url = parseHttpUrl(value);
  if (!url) return null;
  const host = bareHost(url);
  const segments = url.pathname.split('/').filter(Boolean);
  let id = null;
  if (host === 'youtu.be') {
    id = segments[0] || null;
  } else if (YOUTUBE_HOSTS.has(host)) {
    if (segments[0] === 'watch') id = url.searchParams.get('v');
    else if (['embed', 'shorts', 'live', 'v'].includes(segments[0])) id = segments[1] || null;
  }
  return id && id !== 'videoseries' && YOUTUBE_ID_RE.test(id) ? id : null;
}

/**
 * { id, hash } of a Vimeo URL: vimeo.com/<id>[/<hash>], player.vimeo.com/video/<id>[?h=<hash>],
 * vimeo.com/channels/<name>/<id>, vimeo.com/groups/<name>/videos/<id> and
 * vimeo.com/showcase|album/<collectionId>/video/<id> (the video id, not the collection id).
 * null for live events (vimeo.com/event|live/...) and bare showcase/album pages.
 * Keep in sync with amplify/backend/function/getPublicMedia/src/media.js (vimeoRef).
 */
export function vimeoRef(value) {
  const url = parseHttpUrl(value);
  if (!url) return null;
  const host = bareHost(url);
  const segments = url.pathname.split('/').filter(Boolean);
  let id = null;
  let hash = null;
  if (host === 'player.vimeo.com') {
    if (segments[0] === 'video' && /^\d+$/.test(segments[1] || '')) id = segments[1];
  } else if (host === 'vimeo.com' && segments[0] !== 'event' && segments[0] !== 'live') {
    let index = segments.findIndex((s, i) => (s === 'video' || s === 'videos') && /^\d+$/.test(segments[i + 1] || ''));
    if (index >= 0) index += 1;
    else if (segments[0] !== 'showcase' && segments[0] !== 'album') index = segments.findIndex((s) => /^\d+$/.test(s));
    if (index >= 0) {
      id = segments[index];
      const next = segments[index + 1];
      if (next && /^[0-9a-f]+$/i.test(next)) hash = next;
    }
  }
  if (!id) return null;
  const h = hash || url.searchParams.get('h');
  return { id, hash: h && /^[0-9a-f]+$/i.test(h) ? h : null };
}

/**
 * Embeddable player URL for a YouTube / Vimeo URL:
 * https://www.youtube.com/embed/<id> | https://player.vimeo.com/video/<id>[?h=<hash>], else null.
 */
export function videoEmbedUrl(value) {
  const yt = youtubeId(value);
  if (yt) return `https://www.youtube.com/embed/${yt}`;
  const vimeo = vimeoRef(value);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo.id}${vimeo.hash ? `?h=${vimeo.hash}` : ''}`;
  return null;
}

/** "youtube" | "vimeo" | "file" | null - the external URL wins over a video file. */
export function videoProvider(item) {
  const external = item?.externalVideoUrl;
  if (youtubeId(external)) return 'youtube';
  if (vimeoRef(external)) return 'vimeo';
  if (item?.video && (item.video.s3Path || item.video.url || item.video.publicUrl)) return 'file';
  return null;
}

// ---------------------------------------------------------------------------
// Files / uploads
// ---------------------------------------------------------------------------

/** Lower-case extension without dot ('' when none). */
export function fileExtension(name) {
  if (typeof name !== 'string') return '';
  const base = name.split(/[\\/]/).pop();
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

/**
 * S3-safe file name: diacritics removed, every character outside [A-Za-z0-9._-]
 * becomes "-", runs collapsed, at most `maxLength` characters, extension kept
 * (lower-cased). Never empty.
 */
export function sanitizeFileName(name, maxLength = 120) {
  const raw = typeof name === 'string' ? name.split(/[\\/]/).pop().trim() : '';
  const ascii = raw
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');
  const dot = ascii.lastIndexOf('.');
  const hasExt = dot > 0 && dot < ascii.length - 1;
  const ext = hasExt ? ascii.slice(dot + 1).replace(/[^A-Za-z0-9]/g, '').toLowerCase() : '';
  const suffix = ext ? `.${ext}` : '';
  let base = (hasExt ? ascii.slice(0, dot) : ascii)
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  const room = Math.max(1, maxLength - suffix.length);
  base = base.slice(0, room).replace(/[-.]+$/g, '');
  return `${base || 'file'}${suffix}`;
}

/** Storage key (without the "public/" prefix) of a staged audio/video upload. */
export function buildUploadKey(uploadId, fileName) {
  return `${MEDIATHEK_UPLOAD_PREFIX}${uploadId}-${sanitizeFileName(fileName)}`;
}

/** Public https URL of an object key in iec-cp-public (each segment URI-encoded). */
export function publicObjectUrl(key) {
  if (typeof key !== 'string' || !key) return '';
  return `${PUBLIC_BUCKET_URL}/${key.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/')}`;
}

/** Preview URL of a Mediathek image (Image.fileName under mediathekImages/). */
export function mediathekImageUrl(fileName) {
  if (typeof fileName !== 'string' || !fileName) return '';
  return `${PUBLIC_BUCKET_URL}/${MEDIATHEK_IMAGE_PREFIX}${encodeURIComponent(fileName)}`;
}

/** Playable URL of a File: publicUrl, url, else built from s3Path. */
export function mediaFileUrl(file) {
  if (!file) return '';
  return file.publicUrl || file.url || publicObjectUrl(file.s3Path);
}

/** File-input accept attribute for an upload kind ('image' | 'audio' | 'video'). */
export function acceptAttribute(kind) {
  const rules = kind === 'image' ? IMAGE_UPLOAD_RULES : MEDIA_UPLOAD_RULES[kind];
  return rules ? rules.extensions.map((ext) => `.${ext}`).join(',') : '';
}

/** Client-side check of an image upload; English error key or null. */
export function validateImageFile(file) {
  if (!file) return 'Please choose a file';
  const ext = fileExtension(file.name);
  const typeOk = /^image\/(jpeg|png)$/i.test(file.type || '');
  if (ext ? !IMAGE_UPLOAD_RULES.extensions.includes(ext) : !typeOk) return 'Only JPG and PNG images are allowed';
  if (typeof file.size === 'number' && file.size > IMAGE_UPLOAD_RULES.maxBytes) return 'The image must not be larger than 3 MB';
  return null;
}

/** Client-side check of an audio/video upload; English error key or null. */
export function validateMediaFile(file, kind) {
  const rules = MEDIA_UPLOAD_RULES[kind];
  if (!rules) return 'Unknown media type';
  if (!file) return 'Please choose a file';
  if (!rules.extensions.includes(fileExtension(file.name))) {
    return kind === 'audio'
      ? 'Unsupported audio format (allowed: MP3, M4A, WAV, OGG, AAC)'
      : 'Unsupported video format (allowed: MP4, WEBM, MOV, M4V)';
  }
  if (typeof file.size === 'number' && file.size > rules.maxBytes) {
    return kind === 'audio' ? 'The audio file must not be larger than 300 MB' : 'The video file must not be larger than 2000 MB';
  }
  return null;
}

/**
 * File name from the uploadCompanyImage response ("{fileName=<name>}"); throws
 * with the lambda's message for "{error=<message>}" and for empty responses.
 */
export function parseUploadCompanyImageResponse(raw) {
  if (typeof raw !== 'string' || !raw.trim()) throw new Error('Empty response from the image upload');
  if (/error=/.test(raw)) {
    throw new Error(raw.replace(/{|}|error=/g, '').trim() || 'Image upload failed');
  }
  const fileName = raw.replace(/{|}|fileName=/g, '').trim();
  if (!fileName) throw new Error('Empty response from the image upload');
  return fileName;
}

/** true for timeouts / network failures of a GraphQL call (safe to retry an idempotent mutation). */
export function isRetryableUploadError(error) {
  if (!error) return false;
  const parts = [error.name, error.message, error.code, error.errorType];
  if (Array.isArray(error.errors)) {
    for (const e of error.errors) parts.push(e?.message, e?.errorType);
  }
  const text = parts.filter((p) => typeof p === 'string').join(' ');
  if (/upload not found/i.test(text)) return false;
  return /time[ -]?out|timed out|ExecutionTimeout|network|failed to fetch|ECONNRESET|socket hang up|\b50[234]\b|service unavailable|bad gateway/i.test(text);
}

// ---------------------------------------------------------------------------
// Legacy data
// ---------------------------------------------------------------------------

/** AWSJSON value (string, double-encoded string or object) -> object/array; the raw string when not JSON; null when empty. */
export function parseLegacyData(value) {
  if (value === null || value === undefined || value === '') return null;
  let v = value;
  for (let i = 0; i < 2 && typeof v === 'string'; i += 1) {
    try {
      v = JSON.parse(v);
    } catch (e) {
      return v;
    }
  }
  return v;
}

/** Pretty-printed legacy data for the read-only viewer ('' when empty). */
export function formatLegacyData(value) {
  const parsed = parseLegacyData(value);
  if (parsed === null || parsed === undefined) return '';
  return typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2);
}

// ---------------------------------------------------------------------------
// Validation (English i18n keys)
// ---------------------------------------------------------------------------

function hasGermanTitle(title) {
  return getLanguageText(title, 'de').trim() !== '';
}

/** Validation errors of the item form (empty array = valid). */
export function validateMediaItem(item) {
  const errors = [];
  if (!item || typeof item !== 'object') return ['Invalid data'];
  if (!item.mediaSeriesId) errors.push('Please select a series');
  if (!hasGermanTitle(item.title)) errors.push('Please enter a German title');
  if (!MEDIA_STATUSES.includes(item.status)) errors.push('Please select a valid status');
  if (item.publishedAt && timeOrNull(item.publishedAt) === null) errors.push('The publication date is invalid');
  if (!isIntegerLike(item.episodeNumber)) errors.push('The episode number must be a whole number');
  if (!isIntegerLike(item.sortKey)) errors.push('The sort key must be a whole number');
  if (emptyToNull(item.externalVideoUrl) && !isValidHttpUrl(item.externalVideoUrl)) {
    errors.push('The external video URL is not a valid URL');
  }
  return errors;
}

/**
 * true when a (limit 1) mediaItemsByMediaSeries connection shows that the series has
 * items: at least one item, or a nextToken (the first page may be empty after filtering).
 */
export function connectionHasItems(connection) {
  return (connection?.items || []).length > 0 || Boolean(connection?.nextToken);
}

/**
 * The series type is locked while the series has items. As long as the items are
 * loading or could not be loaded, it is unknown whether items exist, so it stays locked.
 * @param {{hasItems?: boolean, isLoadingItems?: boolean, itemsLoadFailed?: boolean}} state
 */
export function isSeriesTypeLocked({ hasItems = false, isLoadingItems = false, itemsLoadFailed = false } = {}) {
  return Boolean(hasItems || isLoadingItems || itemsLoadFailed);
}

/**
 * Validation errors of the series form (empty array = valid).
 * @param {object} series form state
 * @param {{hasItems?: boolean, loadedType?: string|null}} [options]
 */
export function validateMediaSeries(series, { hasItems = false, loadedType = null } = {}) {
  const errors = [];
  if (!series || typeof series !== 'object') return ['Invalid data'];
  if (!MEDIA_SERIES_TYPES.includes(series.type)) errors.push('Please select a type (podcast or video)');
  if (hasItems && loadedType && series.type !== loadedType) {
    errors.push('The type cannot be changed while the series has items');
  }
  if (!hasGermanTitle(series.title)) errors.push('Please enter a German title');
  if (!MEDIA_STATUSES.includes(series.status)) errors.push('Please select a valid status');
  if (!isIntegerLike(series.sortKey)) errors.push('The sort key must be a whole number');
  return errors;
}

// ---------------------------------------------------------------------------
// Mutation inputs
// ---------------------------------------------------------------------------

/** MultiLangString list for a mutation: {language, text} only, empty texts dropped, one entry per language; null when empty. */
export function cleanMultiLang(value, transform = emptyToNull) {
  if (!Array.isArray(value)) return null;
  const out = [];
  for (const entry of value) {
    if (!entry || typeof entry.language !== 'string' || !entry.language) continue;
    if (out.some((e) => e.language === entry.language)) continue;
    const text = transform(entry.text);
    if (text !== null && text !== undefined && text !== '') out.push({ language: entry.language, text });
  }
  return out.length ? out : null;
}

/**
 * Image input ({fileName, alt}) or null without fileName. ImageInput (functions.graphql)
 * has no x4UUID, so that Image field is never sent.
 */
export function cleanImage(image) {
  if (!image || typeof image.fileName !== 'string' || !image.fileName) return null;
  return { fileName: image.fileName, alt: typeof image.alt === 'string' ? image.alt : '' };
}

/** Makes a stored URL acceptable for AWSURL (raw spaces / umlauts are percent-encoded). */
export function safeUrl(value) {
  if (typeof value !== 'string' || !value) return null;
  if (!/[^\x21-\x7e]/.test(value)) return value;
  try {
    return encodeURI(decodeURI(value));
  } catch (e) {
    return encodeURI(value);
  }
}

/** File input ({url, publicUrl, s3Path, fileName, type, name}) or null without s3Path/url. */
export function cleanFile(file) {
  if (!file || typeof file !== 'object') return null;
  if (!file.s3Path && !file.url && !file.publicUrl) return null;
  const out = {};
  const url = safeUrl(file.url);
  const publicUrl = safeUrl(file.publicUrl);
  if (url) out.url = url;
  if (publicUrl) out.publicUrl = publicUrl;
  for (const key of ['s3Path', 'fileName', 'type', 'name']) {
    if (typeof file[key] === 'string' && file[key]) out[key] = file[key];
  }
  return out;
}

function omitNulls(input) {
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out;
}

const MEDIA_ITEM_STRING_FIELDS = [
  'topic', 'guest', 'guestEmail', 'guestPhone', 'host', 'speaker',
  'companyName', 'companyUrl', 'companyId', 'externalVideoUrl', 'spotifyUrl', 'podigeeUrl',
];

/**
 * Create/Update input of a MediaItem from the form state. Whitelisted fields
 * only: never legacyData, legacyKey, series, slug or slugHistory (the hooks add
 * the slug parts), no __typename, '' -> null. On create null values are omitted.
 */
export function buildMediaItemInput(item, { isUpdate = false } = {}) {
  const src = item || {};
  const publishedAt = src.publishedAt && timeOrNull(src.publishedAt) !== null ? new Date(src.publishedAt).toISOString() : null;
  const input = {
    mediaSeriesId: src.mediaSeriesId || null,
    title: cleanMultiLang(src.title) || [],
    excerpt: cleanMultiLang(src.excerpt),
    content: cleanMultiLang(src.content, emptyHtmlToNull),
    episodeNumber: toIntOrNull(src.episodeNumber),
    publishedAt,
    isHero: Boolean(src.isHero),
    status: MEDIA_STATUSES.includes(src.status) ? src.status : DEFAULT_MEDIA_STATUS,
    sortKey: toIntOrNull(src.sortKey),
    image: cleanImage(src.image),
    audio: cleanFile(src.audio),
    video: cleanFile(src.video),
  };
  for (const field of MEDIA_ITEM_STRING_FIELDS) input[field] = emptyToNull(src[field]);
  if (isUpdate) return { id: src.id, ...input };
  return omitNulls(input);
}

/**
 * Create/Update input of a MediaSeries from the form state (same rules as
 * buildMediaItemInput; never items, legacyData, legacyKey, slug, slugHistory).
 */
export function buildMediaSeriesInput(series, { isUpdate = false } = {}) {
  const src = series || {};
  const input = {
    type: src.type || null,
    title: cleanMultiLang(src.title) || [],
    excerpt: cleanMultiLang(src.excerpt),
    description: cleanMultiLang(src.description, emptyHtmlToNull),
    host: emptyToNull(src.host),
    coverImage: cleanImage(src.coverImage),
    status: MEDIA_STATUSES.includes(src.status) ? src.status : DEFAULT_MEDIA_STATUS,
    sortKey: toIntOrNull(src.sortKey),
    industryId: emptyToNull(src.industryId),
  };
  if (isUpdate) return { id: src.id, ...input };
  return omitNulls(input);
}

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

/**
 * Filters the flat item list.
 * @param {object[]} items
 * @param {{seriesId?: string, type?: string, status?: string, search?: string}} filters
 * @param {Record<string, {type?: string}>} [seriesById]
 */
export function filterMediaItems(items, filters = {}, seriesById = {}) {
  const search = typeof filters.search === 'string' ? filters.search.trim().toLowerCase() : '';
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (!item) return false;
    if (filters.seriesId && item.mediaSeriesId !== filters.seriesId) return false;
    if (filters.type && seriesById[item.mediaSeriesId]?.type !== filters.type) return false;
    if (filters.status && (item.status || DEFAULT_MEDIA_STATUS) !== filters.status) return false;
    if (search) {
      const haystack = [
        ...(Array.isArray(item.title) ? item.title.map((e) => e?.text) : []),
        item.guest,
        item.companyName,
      ].filter((s) => typeof s === 'string').join(' ').toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Editor helpers (EditMediaItem / EditMediaSeries layout, item duplicate)
// ---------------------------------------------------------------------------

/**
 * Deterministic JSON: object keys sorted, undefined / function values dropped
 * (like JSON.stringify), circular references replaced by "[Circular]".
 * Used to compare form states (dirty tracking) independent of key order.
 */
export function stableStringify(value) {
  const seen = new WeakSet();
  const walk = (v) => {
    if (v === undefined || typeof v === 'function' || typeof v === 'symbol') return undefined;
    if (typeof v === 'bigint') return JSON.stringify(String(v));
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (typeof v.toJSON === 'function') return walk(v.toJSON());
    if (seen.has(v)) return JSON.stringify('[Circular]');
    seen.add(v);
    let out;
    if (Array.isArray(v)) {
      out = `[${v.map((entry) => {
        const s = walk(entry);
        return s === undefined ? 'null' : s;
      }).join(',')}]`;
    } else {
      const parts = [];
      for (const key of Object.keys(v).sort()) {
        const s = walk(v[key]);
        if (s !== undefined) parts.push(`${JSON.stringify(key)}:${s}`);
      }
      out = `{${parts.join(',')}}`;
    }
    seen.delete(v);
    return out;
  };
  const result = walk(value);
  return result === undefined ? '' : result;
}

/** Number of characters (Unicode code points) of a text; 0 for non-strings. */
export function countChars(text) {
  if (typeof text !== 'string') return 0;
  return Array.from(text).length;
}

/** Host of an http(s) URL without "www." ('' when empty / not a URL). Schemeless input is accepted. */
export function urlHost(value) {
  const url = parseHttpUrl(value);
  return url ? url.hostname.toLowerCase().replace(/^www\./, '') : '';
}

/** YouTube thumbnail (hqdefault) of a YouTube URL, else null - same as the public API's imageUrl fallback. */
export function youtubeThumbnailUrl(value) {
  const id = youtubeId(value);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

/** Displayed file name of a File (fileName, name, else the last segment of s3Path / url; '' when none). */
export function mediaFileName(file) {
  if (!file || typeof file !== 'object') return '';
  if (typeof file.fileName === 'string' && file.fileName) return file.fileName;
  if (typeof file.name === 'string' && file.name) return file.name;
  const source = file.s3Path || file.url || file.publicUrl || '';
  if (typeof source !== 'string' || !source) return '';
  const last = source.split('?')[0].split('/').filter(Boolean).pop() || '';
  try {
    return decodeURIComponent(last);
  } catch (e) {
    return last;
  }
}

/**
 * Public website path of a series (/mediathek/<seriesSlug>, itemSlug omitted) or
 * an item (/mediathek/<seriesSlug>/<itemSlug>); '' when a required slug is missing.
 */
export function publicMediaPath(seriesSlug, itemSlug) {
  const series = typeof seriesSlug === 'string' ? seriesSlug.trim() : '';
  if (!series) return '';
  if (itemSlug === undefined) return `/mediathek/${series}`;
  const item = typeof itemSlug === 'string' ? itemSlug.trim() : '';
  return item ? `/mediathek/${series}/${item}` : '';
}

/**
 * What the website shows for an item - same precedence as getPublicMedia:
 * a recognised YouTube/Vimeo URL ('external') wins over a video file ('videoFile');
 * otherwise image + audio ('imageAudio'), image only ('image'), audio only ('audio')
 * or nothing ('none').
 * @returns {'external'|'videoFile'|'imageAudio'|'image'|'audio'|'none'}
 */
export function stagePreviewSource(item) {
  if (!item || typeof item !== 'object') return 'none';
  if (videoEmbedUrl(item.externalVideoUrl)) return 'external';
  if (mediaFileUrl(item.video)) return 'videoFile';
  const hasImage = Boolean(item.image && typeof item.image.fileName === 'string' && item.image.fileName);
  const hasAudio = Boolean(mediaFileUrl(item.audio));
  if (hasImage && hasAudio) return 'imageAudio';
  if (hasImage) return 'image';
  if (hasAudio) return 'audio';
  return 'none';
}

/** Media slot ids of the item editor ('externalVideo' = externalVideoUrl, 'video' = video file). */
export const MEDIA_SLOTS = ['audio', 'image', 'externalVideo', 'video'];

/**
 * Order of the media slots by series type: podcast -> audio first, video ->
 * video link first, unknown -> image first.
 * @returns {Array<'audio'|'image'|'externalVideo'|'video'>}
 */
export function mediaSlotOrder(seriesType) {
  if (seriesType === 'podcast') return ['audio', 'image', 'externalVideo', 'video'];
  if (seriesType === 'video') return ['externalVideo', 'video', 'image', 'audio'];
  return ['image', 'audio', 'externalVideo', 'video'];
}

/** Title suffix per language of a duplicated item. */
export const COPY_TITLE_SUFFIX = { de: ' (Kopie)', en: ' (Copy)' };

/** Text with `suffix` appended once (unchanged when empty; not repeated when it already ends with the suffix). */
export function appendCopySuffix(text, suffix) {
  if (typeof text !== 'string' || !suffix) return text;
  const trimmed = text.replace(/\s+$/, '');
  if (!trimmed) return text;
  return trimmed.endsWith(suffix.trim()) ? trimmed : `${trimmed}${suffix}`;
}

/** MultiLangString list as plain {language, text} objects (no __typename, invalid entries dropped). */
function copyMultiLang(value, transformText) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((e) => e && typeof e.language === 'string' && e.language)
    .map((e) => {
      const text = typeof e.text === 'string' ? e.text : '';
      return { language: e.language, text: transformText ? transformText(text, e.language) : text };
    });
}

/** Plain deep copy of an Image ('image') / File ('file') object without __typename; null without a file. */
function copyMediaObject(value, kind) {
  if (!value || typeof value !== 'object') return null;
  const hasFile = kind === 'image' ? typeof value.fileName === 'string' && value.fileName !== '' : Boolean(mediaFileUrl(value));
  if (!hasFile) return null;
  return stripTypename(JSON.parse(JSON.stringify(value)));
}

/**
 * Form state (shape of useMediaItemData's toFormState) of a new draft item copied
 * from a saved MediaItem: id, slug, slug history, legacy data and timestamps are
 * not copied; status draft, no hero, empty sort key (shown first), publishedAt now;
 * " (Kopie)" / " (Copy)" appended to the German / English title. Media objects are
 * deep copies that reference the same stored files (clearing a field or deleting an
 * item never deletes stored files).
 * @param {object} source loaded MediaItem (GET_MEDIA_ITEM) or its form state
 * @param {string} [targetSeriesId] series of the copy (default: the source's series)
 * @param {{now?: Date}} [options]
 */
export function buildDuplicateMediaItem(source, targetSeriesId, { now } = {}) {
  const src = source && typeof source === 'object' ? source : {};
  const copy = {
    mediaSeriesId: targetSeriesId || src.mediaSeriesId || '',
    title: copyMultiLang(src.title, (text, language) => appendCopySuffix(text, COPY_TITLE_SUFFIX[language])),
    slug: '',
    slugHistory: [],
    excerpt: copyMultiLang(src.excerpt),
    content: copyMultiLang(src.content),
    episodeNumber: src.episodeNumber === null || src.episodeNumber === undefined ? '' : String(src.episodeNumber),
    publishedAt: nowIsoMinute(now || new Date()),
    isHero: false,
    status: DEFAULT_MEDIA_STATUS,
    sortKey: '',
    image: copyMediaObject(src.image, 'image'),
    audio: copyMediaObject(src.audio, 'file'),
    video: copyMediaObject(src.video, 'file'),
  };
  for (const field of MEDIA_ITEM_STRING_FIELDS) {
    copy[field] = typeof src[field] === 'string' ? src[field] : '';
  }
  return copy;
}

/**
 * Create route of a copy of `sourceItem` (series-scoped, ?copyFrom=<id>);
 * null when the item has no id or no series.
 */
export function duplicateItemPath(sourceItem) {
  const id = sourceItem?.id;
  const seriesId = sourceItem?.mediaSeriesId;
  if (!id || !seriesId) return null;
  return `/admin/mediathek/series/${encodeURIComponent(seriesId)}/items/create?copyFrom=${encodeURIComponent(id)}`;
}
